import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Star } from 'lucide-react';
import { partners, testimonials, stats } from '../mock/mockData';
import { useTranslation } from '../hooks/useTranslation';

const Partners = () => {
  const { t } = useTranslation();
  return (
    <section id="partners" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
            {t('partners.title')}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('partners.subtitle')}
          </p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => {
            const statKeys = ['projectsCompleted', 'partnerCompanies', 'countriesServed', 'materialsCatalog'];
            return (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-light text-gray-900 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600">
                  {t(`partners.stats.${statKeys[index]}`)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Partners Grid */}
        <div className="mb-16">
          <h3 className="text-2xl font-medium text-gray-900 text-center mb-8">
            {t('partners.certifiedPartners')}
          </h3>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {partners.map((partner) => (
              <Card key={partner.id} className="hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="w-full h-16 object-contain mb-4 grayscale hover:grayscale-0 transition-all duration-300"
                  />
                  <h4 className="font-medium text-gray-900 text-sm mb-2">
                    {partner.name}
                  </h4>
                  <div className="flex items-center justify-center text-xs text-gray-600 mb-1">
                    <MapPin size={10} className="mr-1" />
                    {partner.country}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {partner.specialization}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div>
          <h3 className="text-2xl font-medium text-gray-900 text-center mb-8">
            {t('partners.clientTestimonials')}
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={16}
                        className="text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  
                  <blockquote className="text-gray-700 mb-6 leading-relaxed">
                    "{testimonial.content}"
                  </blockquote>
                  
                  <div className="flex items-center">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4"
                    />
                    <div>
                      <div className="font-medium text-gray-900">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {testimonial.role}
                      </div>
                      <div className="text-sm text-gray-500">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Partners;